import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { LoadingComponent } from "../App";
import RenderedHtml from "./renderedHtml";

import searchIcon from "./../media/search-icon.svg";
import SearchHeader from "./SearchHeader";
import pdfIcon from "./../media/orange-PDF.svg";
import "./../styleSheet/main/admin/hr/documents/directives/directives.css";
import docButton from "./../media/doc-orange.svg";
import zipButton from "./../media/zip.svg";
import jpgPhotoButton from "./../media/jpg-photo.svg";
import txtButton from "./../media/txt-orange.svg";
import xlsButton from "./../media/xls-orange.svg";
import mpegVideoButton from "./../media/mpeg-video.svg";
import deleteButtonIcon from "./../media/delete-button.svg";
import editButtonIcon from "./../media/edit-button.svg";
import trashButtonIcon from "./../media/trash-button.svg";
// Ikony przekazuj przez props lub importuj tutaj jeśli uniwersalne

const BenefitDocumentsList = ({
  apiEndpoint,
  sectionTitle,
  createPath,
  editPathPrefix,
  isAdmin,
  showNotification,
  userData,
  deleteButtonIcon,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("title");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [descriptions, setDescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [documentTitle, setDocumentTitle] = useState("");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1350);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    let abortController = new AbortController();

    const fetchPrints = async () => {
      try {
        const response = await apiEndpoint();
        // Sprawdź czy komponent jest nadal zamontowany przed aktualizacją stanu
        if (!isMountedRef.current || abortController.signal.aborted) {
          return;
        }
        const sortedData = (response.data || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setDescriptions(sortedData);
      } catch (err) {
        // Ignoruj błędy jeśli komponent został odmontowany lub żądanie zostało anulowane
        if (!isMountedRef.current || abortController.signal.aborted) {
          return;
        }
        setError(err.message);
      } finally {
        if (isMountedRef.current && !abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchPrints();

    // Cleanup: oznacz komponent jako odmontowany i anuluj żądanie
    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, [apiEndpoint]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchByChange = (criterion) => {
    setSearchBy(criterion);
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1350);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDelete = async (id, title) => {
    setDocumentToDelete(id);
    setDocumentTitle(title);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiEndpoint.delete(documentToDelete);
      setDescriptions((prev) =>
        prev.filter((item) => item.id !== documentToDelete),
      );
      // showNotification("Dokument został pomyślnie usunięty.", "success");
    } catch (err) {
      // showNotification("Nie udało się usunąć dokumentu.", "error");
    } finally {
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      setDocumentTitle("");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
    setDocumentTitle("");
  };

  const filteredDescriptions = descriptions.filter((item) => {
    if (isSmallScreen) {
      const title = item.title?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";
      const date = item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("pl-PL").toLowerCase()
        : "";
      const term = searchTerm.toLowerCase();
      return (
        title.includes(term) ||
        description.includes(term) ||
        date.includes(term)
      );
    } else {
      if (searchBy === "date") {
        const dateString = item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("pl-PL")
          : "";
        return dateString.toLowerCase().includes(searchTerm.toLowerCase());
      } else {
        return item[searchBy]?.toLowerCase().includes(searchTerm.toLowerCase());
      }
    }
  });

  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    return createPortal(
      <div className="confirmation-modal">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={cancelDelete}
          />
          <div className="areYouSure">
            <p>
              {t("documentList.confirmDeleteDocument")}{" "}
              <strong>{documentTitle}</strong>?
            </p>
          </div>
          <div className="confirmation-buttons">
            <button onClick={confirmDelete}>{t("common.yes")}</button>
            <button onClick={cancelDelete}>{t("common.no")}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  // Helper to resolve icon for display (including video)
  const getIconForDisplay = (icon) => {
    if (!icon) return txtButton;
    if (icon === "jpgPhotoButton") return jpgPhotoButton;
    if (icon === "pdfButton") return pdfIcon;
    if (icon === "docButton") return docButton;
    if (icon === "xlsButton") return xlsButton;
    if (icon === "zipButton") return zipButton;
    if (icon === "mpegVideoButton") return mpegVideoButton;
    if (icon === "txtButton") return txtButton;
    return txtButton;
  };

  return (
    <div className="directives-container">
      <SearchHeader
        title={sectionTitle}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        searchBy={searchBy}
        isDropdownOpen={isDropdownOpen}
        toggleDropdown={toggleDropdown}
        dropdownRef={dropdownRef}
        handleSearchByChange={handleSearchByChange}
        dropdownOptions={[
          { value: "title", label: t("documentList.title") },
          { value: "date", label: t("common.date") },
        ]}
        placeholder={
          searchBy === "title"
            ? t("documentList.searchByTitle")
            : t("documentList.searchByDate")
        }
        searchIcon={searchIcon}
        isSmallScreen={isSmallScreen}
      >
        {isAdmin(userData) && (
          <NavLink className="create-button" to={createPath}>
            {t("documentList.createNewDocument")}
          </NavLink>
        )}
      </SearchHeader>
      {loading ? (
        <LoadingComponent />
      ) : error ? (
        <div className="error-message">
          {t("common.error")}: {error}
        </div>
      ) : (
        filteredDescriptions.map((item, index) => (
          <div key={index} className="directives-items">
            <div className="directives-header">
              <div className="directives-item">
                <h2 className="directives-title">{item.title}</h2>
                {isAdmin(userData) && (
                  <div className="admin-buttons">
                    <button
                      className="edit-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${editPathPrefix}${item.id}`);
                      }}
                    >
                      <img
                        src={editButtonIcon}
                        alt={t("common.edit")}
                        style={{ width: 38, height: 38 }}
                      />
                    </button>
                    <button
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id, item.title);
                      }}
                    >
                      <img
                        src={trashButtonIcon}
                        alt={t("common.delete")}
                        style={{ width: 38, height: 38 }}
                      />
                    </button>
                  </div>
                )}
              </div>
              <div className="title-line"></div>
            </div>
            <div className="directives-box-inside">
              {item.preSignedFileUrl ? (
                <a
                  href={item.preSignedFileUrl}
                  download
                  className="download-btn"
                >
                  <img
                    src={getIconForDisplay(item.icon)}
                    alt="File Icon"
                    className="pdf-icon"
                  />
                </a>
              ) : (
                <img
                  src={getIconForDisplay(item.icon)}
                  alt="File Icon"
                  className="pdf-icon"
                />
              )}
              <div className="directives-text">
                <div className="directives-description">
                  <RenderedHtml html={item.description} />
                </div>
                <p className="directives-date">
                  {item.createdBy},{" "}
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString("pl-PL")
                    : ""}
                </p>
              </div>
            </div>
          </div>
        ))
      )}
      {renderDeleteModal()}
    </div>
  );
};

export default BenefitDocumentsList;
